import { useState } from "react";
import {
  useCreate,
  useDelete,
  useListContext,
  useUpdate,
} from "ra-core";
import { useDelayedRefresh } from "./useDelayedRefresh";
import { Trash2, Plus, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type FreightRow = {
  id: number;
  load_id: number;
  invoice_number?: string;
  price?: number;
  currency?: string;
  price_sell?: number;
  currency_sell?: string;
  description?: string;
  freight_notes?: string;
};

const CURRENCY_OPTIONS = ["USD", "CAD", "AUD", "GBP", "EUR"];

const Sel = ({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
}) => (
  <Select value={value || ""} onValueChange={onChange}>
    <SelectTrigger className="h-7 text-sm">
      <SelectValue placeholder={placeholder ?? "Select..."} />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o} value={o}>
          {o}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

type EditableFreight = {
  invoice_number: string;
  price: string;
  currency: string;
  price_sell: string;
  currency_sell: string;
  description: string;
};

const EMPTY_ROW: EditableFreight = {
  invoice_number: "",
  price: "",
  currency: "USD",
  price_sell: "",
  currency_sell: "USD",
  description: "",
};

const rowToFreight = (v: EditableFreight): Partial<FreightRow> => ({
  invoice_number: v.invoice_number || undefined,
  price: v.price ? Number(v.price) : undefined,
  currency: v.currency || "USD",
  price_sell: v.price_sell ? Number(v.price_sell) : undefined,
  currency_sell: v.currency_sell || "USD",
  description: v.description || undefined,
});

const itemToRow = (item: FreightRow): EditableFreight => ({
  invoice_number: item.invoice_number ?? "",
  price: item.price != null ? String(item.price) : "",
  currency: item.currency ?? "USD",
  price_sell: item.price_sell != null ? String(item.price_sell) : "",
  currency_sell: item.currency_sell ?? "USD",
  description: item.description ?? "",
});

const EditableFields = ({
  values,
  set,
}: {
  values: EditableFreight;
  set: (f: keyof EditableFreight, v: string) => void;
}) => (
  <>
    <td className="p-1 w-32">
      <Input value={values.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} placeholder="Invoice #" className="h-7 text-sm" />
    </td>
    <td className="p-1 w-24">
      <Input value={values.price} onChange={(e) => set("price", e.target.value)} type="number" placeholder="Buy Price" className="h-7 text-sm" />
    </td>
    <td className="p-1 w-20">
      <Sel value={values.currency} onChange={(v) => set("currency", v)} options={CURRENCY_OPTIONS} />
    </td>
    <td className="p-1 w-24">
      <Input value={values.price_sell} onChange={(e) => set("price_sell", e.target.value)} type="number" placeholder="Sell Price" className="h-7 text-sm" />
    </td>
    <td className="p-1 w-20">
      <Sel value={values.currency_sell} onChange={(v) => set("currency_sell", v)} options={CURRENCY_OPTIONS} />
    </td>
    <td className="p-1 min-w-40">
      <Input value={values.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" className="h-7 text-sm" />
    </td>
  </>
);

const FreightRowComponent = ({
  item,
  onDelete,
  onSave,
}: {
  item: FreightRow;
  onDelete: (id: number) => void;
  onSave: (id: number, data: Partial<FreightRow>) => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<EditableFreight>(() => itemToRow(item));
  const set = (field: keyof EditableFreight, value: string) =>
    setValues((v) => ({ ...v, [field]: value }));

  if (editing) {
    return (
      <tr className="border-b bg-muted/30">
        <EditableFields values={values} set={set} />
        <td className="p-1 w-16">
          <div className="flex gap-1">
            <Button size="icon" variant="ghost" className="h-7 w-7"
              onClick={() => { onSave(item.id, rowToFreight(values)); setEditing(false); }}>
              <Check className="h-3.5 w-3.5 text-green-600" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7"
              onClick={() => { setValues(itemToRow(item)); setEditing(false); }}>
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => setEditing(true)}>
      <td className="p-2 text-sm">{item.invoice_number || "—"}</td>
      <td className="p-2 text-sm text-right text-blue-600 dark:text-blue-400 font-medium">
        {item.price != null ? Number(item.price).toFixed(2) : "—"}
      </td>
      <td className="p-2 text-sm text-blue-600 dark:text-blue-400">{item.currency ?? "—"}</td>
      <td className="p-2 text-sm text-right text-green-600 dark:text-green-400 font-medium">
        {item.price_sell != null ? Number(item.price_sell).toFixed(2) : "—"}
      </td>
      <td className="p-2 text-sm text-green-600 dark:text-green-400">{item.currency_sell ?? "—"}</td>
      <td className="p-2 text-sm text-muted-foreground">{item.description || ""}</td>
      <td className="p-2 w-10">
        <Button size="icon" variant="ghost" className="h-7 w-7"
          onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}>
          <Trash2 className="h-3.5 w-3.5 text-destructive" />
        </Button>
      </td>
    </tr>
  );
};

const NewFreightRow = ({
  loadId,
  onCreated,
}: {
  loadId: number;
  onCreated: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<EditableFreight>({ ...EMPTY_ROW });
  const [create] = useCreate();
  const refresh = useDelayedRefresh();
  const set = (field: keyof EditableFreight, value: string) =>
    setValues((v) => ({ ...v, [field]: value }));

  const handleSave = () => {
    create(
      "load_freight",
      { data: { load_id: loadId, ...rowToFreight(values) } },
      {
        onSuccess: () => {
          setValues({ ...EMPTY_ROW });
          setOpen(false);
          onCreated();
          refresh();
        },
      }
    );
  };

  if (!open) {
    return (
      <tr>
        <td colSpan={7} className="p-1">
          <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground gap-1.5"
            onClick={() => setOpen(true)}>
            <Plus className="h-3.5 w-3.5" />
            Add freight
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b bg-muted/20">
      <EditableFields values={values} set={set} />
      <td className="p-1 w-16">
        <div className="flex gap-1">
          <Button size="icon" variant="ghost" className="h-7 w-7" onClick={handleSave}>
            <Check className="h-3.5 w-3.5 text-green-600" />
          </Button>
          <Button size="icon" variant="ghost" className="h-7 w-7"
            onClick={() => { setValues({ ...EMPTY_ROW }); setOpen(false); }}>
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

export const FreightTable = ({ loadId }: { loadId: number }) => {
  const { data = [], refetch } = useListContext<FreightRow>();
  const [deleteOne] = useDelete();
  const [update] = useUpdate();
  const refresh = useDelayedRefresh();

  const handleDelete = (id: number) => {
    deleteOne(
      "load_freight",
      { id, previousData: data.find((i) => i.id === id) },
      { onSuccess: () => { refetch(); refresh(); } }
    );
  };

  const handleSave = (id: number, patch: Partial<FreightRow>) => {
    update(
      "load_freight",
      { id, data: patch, previousData: data.find((i) => i.id === id) },
      { onSuccess: () => { refetch(); refresh(); } }
    );
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b text-muted-foreground text-xs">
            <th className="text-left p-2 font-medium w-32">Invoice #</th>
            <th className="text-right p-2 font-medium w-28 text-blue-600 dark:text-blue-400">Buy Price</th>
            <th className="text-left p-2 font-medium w-20 text-blue-600 dark:text-blue-400">Currency</th>
            <th className="text-right p-2 font-medium w-28 text-green-600 dark:text-green-400">Sell Price</th>
            <th className="text-left p-2 font-medium w-20 text-green-600 dark:text-green-400">Currency</th>
            <th className="text-left p-2 font-medium">Description</th>
            <th className="w-10" />
          </tr>
        </thead>
        <tbody>
          {data.map((item) => (
            <FreightRowComponent
              key={item.id}
              item={item}
              onDelete={handleDelete}
              onSave={handleSave}
            />
          ))}
          <NewFreightRow loadId={loadId} onCreated={refetch} />
        </tbody>
      </table>
    </div>
  );
};
